import React, { useState, useCallback, useMemo, type ReactElement, useEffect } from 'react';
import { isUndefined, isEmpty } from 'lodash';
import { Dropdown, DropdownAnchor, DropdownContent, DropdownMenu, type Values } from '@fiverr-private/dropdown';
import type { SelectProps } from './types';
import type { OptionProps } from './Option/types';
import { SELECT_NAME, SELECT_SIZES } from './constants';
import Trigger from './Trigger';
import { getSelectedOptionLabel } from './utils';

const Select = ({
  disabled,
  children,
  values,
  defaultValues,
  name = SELECT_NAME,
  onChange,
  placeholder,
  isSuccess,
  isError,
  hideChevron = false,
  size = SELECT_SIZES.default,
  isOpen,
  onOpenChanged,
  initialOpen,
  disableFlip,
  position,
  fullWidth,
  onTriggerFocus,
  onTriggerBlur,
}: SelectProps): React.JSX.Element => {
  const controlled = !isUndefined(values);
  const options = useMemo(() => React.Children.toArray(children), [children]) as ReactElement<OptionProps>[];
  const [selectedLabel, setSelectedLabel] = useState<string>();

  const handleChange = useCallback(
    (newValues: Values) => {
      if (controlled) {
        onChange?.(newValues);
        return;
      }

      const label = getSelectedOptionLabel(newValues, options);
      setSelectedLabel(label);

      onChange?.(newValues);
    },
    [onChange, controlled, options]
  );

  useEffect(() => {
    if (defaultValues && !selectedLabel) {
      const label = getSelectedOptionLabel(defaultValues, options);
      setSelectedLabel(label);
    }
  }, [options, defaultValues, selectedLabel]);

  useEffect(() => {
    if (!controlled || isEmpty(values)) {
      return;
    }

    const label = getSelectedOptionLabel(values, options);
    setSelectedLabel(label);
  }, [options, values, controlled]);

  return (
    <Dropdown
      defaultValues={defaultValues}
      values={values}
      onChange={handleChange}
      searchWhenClosed={true}
      position={position}
      isOpen={isOpen}
      onOpenChanged={onOpenChanged}
      initialOpen={initialOpen}
      disableFlip={disableFlip}
    >
      <DropdownAnchor fullWidth={fullWidth} disabled={disabled} onFocus={onTriggerFocus} onBlur={onTriggerBlur}>
        <Trigger
          placeholder={placeholder}
          name={name}
          isSuccess={isSuccess}
          isError={isError}
          disabled={disabled}
          size={size}
          hideChevron={hideChevron}
          fullWidth={fullWidth}
        >
          {selectedLabel}
        </Trigger>
      </DropdownAnchor>
      <DropdownContent>
        <DropdownMenu>{children}</DropdownMenu>
      </DropdownContent>
    </Dropdown>
  );
};
export default Select;
